import i18n from "@/i18n";
import { ApiError, API_CODES, type FieldErrors } from "./errors";
import { tokenStore } from "./tokenStore";

export const API_BASE = (import.meta.env.VITE_APP_API_BASE || "/api/v1").replace(/\/+$/, "");
const CSRF_COOKIE = "cp_csrf";
const REFRESH_LOCK = "cureplus-token-refresh";

type DssResponse = {
  response_code: string;
  response_status: "Success" | "Failed";
  response_msg: string;
  request_id?: string;
  errors?: { field: string; message: string }[];
  details?: Record<string, unknown>;
};
type Envelope<T> = { object: T | null; dss_response?: DssResponse };

export type ApiResult<T> = { data: T; message: string; code: string };

export type RequestOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  /** Attach the bearer token and transparently refresh it on expiry. */
  auth?: boolean;
  headers?: Record<string, string>;
};

export function readCookie(name: string): string | null {
  const match = document.cookie.split("; ").find((part) => part.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

function buildUrl(path: string, query?: RequestOptions["query"]) {
  const url = `${API_BASE}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== "") params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function send<T>(path: string, options: RequestOptions): Promise<ApiResult<T>> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    "X-Language": i18n.language || "en",
    ...options.headers,
  };
  if (options.body !== undefined) headers["Content-Type"] = "application/json";
  const token = tokenStore.get();
  if (options.auth !== false && token) headers.Authorization = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method: options.method ?? "GET",
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      credentials: "same-origin",
      signal: options.signal,
    });
  } catch (error) {
    if ((error as Error).name === "AbortError") throw error;
    throw new ApiError({ status: 0, code: "NETWORK", message: i18n.t("errors.network") });
  }

  const payload = (await response.json().catch(() => ({}))) as Envelope<T>;
  const dss = payload.dss_response;
  if (!response.ok) {
    const fieldErrors: FieldErrors = {};
    for (const item of dss?.errors ?? []) fieldErrors[item.field] ??= item.message;
    const retryAfter = Number(response.headers.get("Retry-After"));
    throw new ApiError({
      status: response.status,
      code: dss?.response_code ?? `HTTP-${response.status}`,
      message: dss?.response_msg || i18n.t("errors.generic"),
      fieldErrors,
      requestId: dss?.request_id,
      retryAfterSeconds: Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined,
      details: dss?.details,
    });
  }
  return { data: payload.object as T, message: dss?.response_msg ?? "", code: dss?.response_code ?? "" };
}

// -----------------------------------------------------------------------------
// Token refresh: single-flight within the tab and serialised across tabs with
// the Web Locks API, so concurrent tabs never replay a rotated refresh token.
// -----------------------------------------------------------------------------

export type AuthResultPayload = { accessToken: string; expiresIn: number; me?: unknown };
let inflightRefresh: Promise<ApiResult<AuthResultPayload>> | null = null;

export function refreshSession(): Promise<ApiResult<AuthResultPayload>> {
  if (!inflightRefresh) {
    const run = async () => {
      const csrf = readCookie(CSRF_COOKIE);
      if (!csrf) {
        throw new ApiError({ status: 401, code: API_CODES.REFRESH_INVALID, message: i18n.t("errors.sessionEnded") });
      }
      const result = await send<AuthResultPayload>("/auth/refresh", {
        method: "POST",
        auth: false,
        headers: { "X-CSRF-Token": csrf },
      });
      tokenStore.set(result.data.accessToken);
      return result;
    };
    const locked: Promise<ApiResult<AuthResultPayload>> =
      typeof navigator !== "undefined" && navigator.locks
        ? // lib.dom types the callback result as nested; `then` flattens it.
          navigator.locks.request(REFRESH_LOCK, run).then((result) => result)
        : run();
    inflightRefresh = locked.finally(() => {
      inflightRefresh = null;
    });
    return inflightRefresh;
  }
  return inflightRefresh;
}

const REFRESHABLE = new Set<string>([API_CODES.TOKEN_EXPIRED]);

/** Performs an API call, refreshing the access token once when it has expired. */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<ApiResult<T>> {
  try {
    return await send<T>(path, options);
  } catch (error) {
    const expired = error instanceof ApiError && error.status === 401 && REFRESHABLE.has(error.code);
    if (!expired || options.auth === false) {
      if (error instanceof ApiError && error.status === 401 && options.auth !== false && tokenStore.get()) {
        tokenStore.notifySessionExpired();
      }
      throw error;
    }
    try {
      await refreshSession();
    } catch (refreshError) {
      tokenStore.notifySessionExpired();
      throw refreshError;
    }
    return send<T>(path, options);
  }
}

/**
 * Downloads a binary file (e.g. a PDF slip) with the bearer token and saves it
 * through a temporary object URL. Refreshes an expired token once, like apiRequest.
 */
export async function downloadFile(path: string, fallbackName: string, options: { headers?: Record<string, string>; auth?: boolean } = {}): Promise<void> {
  const auth = options.auth !== false;
  const fetchFile = () =>
    fetch(`${API_BASE}${path}`, {
      headers: { ...(auth ? { Authorization: `Bearer ${tokenStore.get() ?? ""}` } : {}), "X-Language": i18n.language || "en", ...options.headers },
      credentials: "same-origin",
    });
  let response = await fetchFile();
  if (response.status === 401 && auth) {
    await refreshSession();
    response = await fetchFile();
  }
  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as Envelope<null>;
    throw new ApiError({ status: response.status, code: payload.dss_response?.response_code ?? `HTTP-${response.status}`, message: payload.dss_response?.response_msg || i18n.t("errors.generic") });
  }
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? fallbackName;
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, "method" | "body">) => apiRequest<T>(path, { ...options, method: "GET" }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) =>
    apiRequest<T>(path, { ...options, method: "POST", body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) =>
    apiRequest<T>(path, { ...options, method: "PATCH", body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, "method" | "body">) => apiRequest<T>(path, { ...options, method: "DELETE" }),
};
